import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../lib/api'
import { clampPlanHeight, parsePlanFrameMessage, planMessage } from '../lib/response-plan-bridge'
import type { PlanNote } from '../types'
import { SpinnerGap } from './ui/icons'
import { Alert } from './ui'

/**
 * The Response Plan tab: the customer's plan page (the same built HTML as
 * the claude.ai artifact) in a sandboxed iframe.
 *
 * Sandbox: allow-scripts, allow-popups and allow-popups-to-escape-sandbox only
 * (links open as normal tabs). Without allow-same-origin the
 * page runs in an opaque origin, so it cannot read the portal session or call
 * portal APIs. The Worker serves the page with its own CSP (sandbox,
 * connect-src 'none', frame-ancestors 'self'). The page asks this component
 * for notes and sends notes through postMessage; this component checks every
 * message and makes the authenticated request. See src/lib/response-plan-bridge.ts.
 */
export function ResponsePlanTab({ publicId, updatedAt }: { publicId: string; updatedAt: string }) {
  const frameRef = useRef<HTMLIFrameElement>(null)
  const loadsRef = useRef(0)
  const [frameKey, setFrameKey] = useState(0)
  const [height, setHeight] = useState(1_200)
  const [loaded, setLoaded] = useState(false)
  const [ready, setReady] = useState(false)
  const [failed, setFailed] = useState(false)
  const src = `/api/workspaces/${encodeURIComponent(publicId)}/response-plan/document?v=${encodeURIComponent(updatedAt)}`

  const post = useCallback((payload: Record<string, unknown>) => {
    // The frame's origin is opaque ("null"), so '*' is the only target that
    // reaches it. The message goes to this exact frame window only, and the
    // frame cannot navigate to another page (see onLoad).
    frameRef.current?.contentWindow?.postMessage(planMessage(payload), '*')
  }, [])

  const sendNotes = useCallback(async () => {
    try {
      const { notes, canSend } = await api.listPlanNotes(publicId)
      post({ action: 'notes', notes: notes satisfies PlanNote[], canSend })
    } catch {
      post({ action: 'notes', notes: [], canSend: false, error: 'Notes could not load. Refresh the page to try again.' })
    }
  }, [post, publicId])

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      const frameWindow = frameRef.current?.contentWindow
      if (!frameWindow || event.source !== frameWindow) return
      const message = parsePlanFrameMessage(event.data)
      if (!message) return
      if (message.action === 'ready') { setReady(true); void sendNotes(); return }
      if (message.action === 'height') { setHeight(clampPlanHeight(message.height)); return }
      if (message.action === 'invalid') { post({ action: 'sent', requestId: message.requestId, ok: false, error: message.error }); return }
      void (async () => {
        try {
          await api.sendPlanNote(publicId, message.sectionKey, message.body)
          post({ action: 'sent', requestId: message.requestId, ok: true })
          await sendNotes()
        } catch (cause) {
          post({ action: 'sent', requestId: message.requestId, ok: false, error: cause instanceof Error ? cause.message : 'The note could not be sent.' })
        }
      })()
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [post, publicId, sendNotes])

  // An error page (expired session, removed plan) still fires onLoad, so the
  // tab waits for the plan's 'ready' message and shows an error without it.
  useEffect(() => {
    if (!loaded || ready) return
    const timer = window.setTimeout(() => setFailed(true), 10_000)
    return () => window.clearTimeout(timer)
  }, [loaded, ready])

  function onLoad() {
    loadsRef.current += 1
    // The plan opens every link in a new tab. If the frame still navigates
    // away, load the plan again so no other page sits in the frame.
    if (loadsRef.current > 1) {
      loadsRef.current = 0
      setFrameKey((key) => key + 1)
      return
    }
    setLoaded(true)
  }

  return (
    <section className="standard-tab response-plan-tab">
      {!loaded && !failed && (
        <div className="loading-lockup" role="status"><SpinnerGap size={18} className="spin" aria-hidden="true" />Loading the response plan</div>
      )}
      {failed && <Alert tone="danger" title="The Response Plan Did Not Load" description="Refresh the page to try again. If it still does not load, contact your Prentus team." />}
      <iframe
        key={frameKey}
        ref={frameRef}
        className="response-plan-frame"
        title="Response Plan"
        src={src}
        sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
        referrerPolicy="same-origin"
        style={{ height }}
        onLoad={onLoad}
        hidden={failed}
      />
    </section>
  )
}
